import { Maatregel, RijdtMaatregel, VsmStatus } from "@/entities/vsm";
import { dismissToast, toastWithUndo } from "@/shared/components";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { VsmEditorContext, VsmEditorContextType } from "./vsm-editor-context";

type VsmEditorProviderProps = {
    children: ReactNode;
    initialMaatregelen: Maatregel[];
    initialStatus?: VsmStatus;
    initialToelichting?: string;
    // Overrides which Rijdt-maatregelen are merged into the RijdtGroep.
    includeInRijdtGroep?: (maatregel: Maatregel) => maatregel is RijdtMaatregel;
};

const removeToelichtingToast = {
    huidig: "Toelichting huidige versie genegeerd",
    spoorweb: "Suggestie toelichting Spoorweb verwijderd",
    geen: "Toelichting verwijderd",
};

export function VsmEditorProvider({
    children,
    initialMaatregelen,
    initialStatus,
    initialToelichting,
    includeInRijdtGroep,
}: Readonly<VsmEditorProviderProps>) {
    const [currentlyEditingMaatregel, setCurrentlyEditingMaatregel] = useState<Maatregel | null>(null);
    const [maatregelen, setMaatregelen] = useState<Maatregel[]>(() => Maatregel.withRijdtGroep(initialMaatregelen, includeInRijdtGroep));
    const [status, setStatus] = useState<VsmStatus>(initialStatus ?? "Definitief");
    const [toelichting, setToelichting] = useState(initialToelichting ?? "");

    const resetToelichting = useCallback(() => {
        setToelichting("");
    }, []);

    const reset = useCallback(() => {
        setMaatregelen(Maatregel.withRijdtGroep(initialMaatregelen, includeInRijdtGroep));
        setCurrentlyEditingMaatregel(null);
    }, [initialMaatregelen, includeInRijdtGroep]);

    const addMaatregel = useCallback(
        (maatregel: Maatregel) => {
            setMaatregelen((prev) => Maatregel.withRijdtGroep([...prev, maatregel], includeInRijdtGroep));
        },
        [includeInRijdtGroep]
    );

    const updateMaatregel = useCallback(
        (maatregel: Maatregel) => {
            setMaatregelen((prev) => {
                const updated = prev.map((m) => (m.id === maatregel.id ? maatregel : m));
                return Maatregel.withRijdtGroep(updated, includeInRijdtGroep);
            });
        },
        [includeInRijdtGroep]
    );

    const setEditingMaatregel = useCallback(
        (maatregel: Maatregel | null) => {
            if (maatregel) {
                const editableMaatregel = Maatregel.editable(maatregel);
                setCurrentlyEditingMaatregel(editableMaatregel);
                updateMaatregel(editableMaatregel);
            } else {
                setCurrentlyEditingMaatregel(null);
            }
        },
        [updateMaatregel]
    );

    const deleteMaatregel = useCallback(
        (maatregelId: string) => {
            const deletedMaatregel = maatregelen.find((m) => m.id === maatregelId);
            const updated = Maatregel.withRijdtGroep(
                maatregelen.filter((m) => m.id !== maatregelId),
                includeInRijdtGroep
            );
            setMaatregelen(updated);
            // Stop editing when the maatregel being edited is gone (deleted itself, or an emptied RijdtGroep)
            setCurrentlyEditingMaatregel((editing) => (editing && updated.some((m) => m.id === editing.id) ? editing : null));
            const toastId = toastWithUndo("Maatregel verwijderd", undefined, () => {
                if (deletedMaatregel) {
                    addMaatregel(deletedMaatregel);
                    dismissToast(toastId);
                }
            });
        },
        [addMaatregel, includeInRijdtGroep, maatregelen]
    );

    const acceptToelichting = useCallback(
        (maatregelId: string) => {
            const maatregel = maatregelen.find((m) => m.id === maatregelId);
            if (maatregel) {
                updateMaatregel(Maatregel.acceptToelichting(maatregel));
            }
        },
        [maatregelen, updateMaatregel]
    );

    const removeToelichting = useCallback(
        (maatregelId: string) => {
            const maatregel = maatregelen.find((m) => m.id === maatregelId);
            if (!maatregel) {
                return;
            }
            updateMaatregel(Maatregel.removeToelichting(maatregel));
            const title = removeToelichtingToast[Maatregel.pendingToelichtingSuggestie(maatregel) ?? "geen"];
            const toastId = toastWithUndo(title, undefined, () => {
                updateMaatregel(maatregel);
                dismissToast(toastId);
            });
        },
        [maatregelen, updateMaatregel]
    );

    const copyToNieuweVersie = useCallback(
        (maatregel: Maatregel) => {
            if (!maatregelen.map((m) => m.id).includes(maatregel.id)) {
                addMaatregel(maatregel);
            }
        },
        [addMaatregel, maatregelen]
    );

    const isCopiedToNieuweVersie = useCallback((maatregel: Maatregel) => maatregelen.some((m) => m.id === maatregel.id), [maatregelen]);

    const contextValue = useMemo(
        (): VsmEditorContextType => ({
            maatregelen,
            editingMaatregel: currentlyEditingMaatregel,
            setEditingMaatregel,
            addMaatregel,
            updateMaatregel,
            deleteMaatregel,
            acceptToelichting,
            removeToelichting,
            copyToNieuweVersie,
            isCopiedToNieuweVersie,
            reset,
            includeInRijdtGroep,
            toelichting,
            setToelichting,
            resetToelichting,
            status,
            setStatus,
        }),
        [
            maatregelen,
            currentlyEditingMaatregel,
            setEditingMaatregel,
            addMaatregel,
            updateMaatregel,
            deleteMaatregel,
            acceptToelichting,
            removeToelichting,
            copyToNieuweVersie,
            isCopiedToNieuweVersie,
            reset,
            includeInRijdtGroep,
            toelichting,
            setToelichting,
            status,
            setStatus,
            resetToelichting,
        ]
    );

    return <VsmEditorContext value={contextValue}>{children}</VsmEditorContext>;
}
